import { useMemo } from "react";
import { Box, Typography } from "@mui/material";

import { tokens } from "../../theme";
import GeographyChart from "../../components/GeographyChart";
import TargetMet from "../../components/targets/TargetMet";
import LoadingState from "../../components/LoadingState";
import PageGuard from "../../components/PageGuard";

import { useAuth } from "../../AuthContext";
import { useRedfidViews } from "../../utils/redfidView";
import regionsData from "../../data/regions.json";
import { REGION_ORDER_NORTH_TO_SOUTH, REGION_TO_CODE } from "../../utils/mapers";
import { cleanCountRows, countRowsToItems, sortCountRows } from "../../utils/utils";

const PARTICIPANT_STAT = {
  key: "participantes",
  label: { singular: "Participante", plural: "Participantes" },
};

function buildStats(row) {
  const universidades = cleanCountRows(row?.universidades);
  const titulos = sortCountRows(cleanCountRows(row?.titulos));
  const programas = sortCountRows(cleanCountRows(row?.programas));
  const participantes = (Array.isArray(row?.participantes) ? row.participantes : [])
    .map((name) => String(name ?? "").trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }));

  return [
    {
      ...PARTICIPANT_STAT,
      value: Number(row?.formadores_qtty ?? 0) || 0,
      items: participantes,
    },
    {
      key: "universidades",
      label: { singular: "Universidad", plural: "Universidades" },
      value: universidades.length,
      items: countRowsToItems(universidades),
    },
    {
      key: "título profesional",
      label: { singular: "Título profesional", plural: "Títulos profesionales" },
      value: titulos.length,
      items: countRowsToItems(titulos),
    },
    {
      key: "programas",
      label: { singular: "Programa", plural: "Programas" },
      value: programas.length,
      items: countRowsToItems(programas),
    },
  ];
}

const Regiones = () => {
  const auth = useAuth();
  const colors = tokens();
  const { loading, error, data } = useRedfidViews();
  const isChile = data?.country === "chile";
  const canSeeParticipantsList = Boolean(auth?.user?.is_admin || auth?.user?.is_staff);

  const regionNameById = useMemo(() => {
    const names = {};
    const list = Array.isArray(regionsData?.regions) ? regionsData.regions : [];

    list.forEach((region) => {
      const roman = String(region?.code ?? "").trim().toUpperCase();
      const name = String(region?.name ?? "").trim();
      const info = REGION_TO_CODE[roman];
      if (name && info) names[String(info.code)] = name;
    });

    return names;
  }, []);

  const regionRows = useMemo(
    () => (Array.isArray(data?.regiones) ? data.regiones : []),
    [data?.regiones]
  );

  const countryRows = useMemo(
    () => (Array.isArray(data?.paises) ? data.paises : []),
    [data?.paises]
  );

  const mapData = useMemo(
    () =>
      regionRows
        .filter((row) => String(row?.id ?? "—") !== "—")
        .map((row) => ({
          id: String(row.id),
          value: Number(row.formadores_qtty ?? 0) || 0,
        })),
    [regionRows]
  );

  const cards = useMemo(() => {
    if (!isChile) {
      return countryRows.map((row) => ({
        id: row.id,
        title: row.pais,
        stats: buildStats(row),
      }));
    }

    const rank = Object.fromEntries(
      REGION_ORDER_NORTH_TO_SOUTH.map((id, index) => [id, index])
    );

    return regionRows
      .map((row) => {
        const id = String(row?.id ?? "—");
        const idNum = Number.parseInt(id, 10);
        const noRegion = id === "—";

        return {
          id,
          title: noRegion
            ? "Sin información"
            : regionNameById[id] ??
            row?.region_name ??
            `Región ${row?.region_roman ?? id}`,
          rank: noRegion
            ? 1000
            : Number.isFinite(idNum)
              ? rank[idNum] ?? 999
              : 999,
          stats: buildStats(row),
        };
      })
      .sort((a, b) => a.rank - b.rank || a.title.localeCompare(b.title, "es"));
  }, [countryRows, isChile, regionNameById, regionRows]);

  if (loading) return <LoadingState message="" />;
  if (error) return <div>Error cargando datos: {String(error)}</div>;

  const pageTitle = isChile ? "REGIONES" : "PAÍSES";
  const subtitle = isChile ? "Detalle por región" : "Detalle por país";

  return (
    <PageGuard title={pageTitle} subtitle={subtitle}>
      <Box m="20px" ml="5px" pb="100px">
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          gridAutoRows="minmax(180px, auto)"
          gap="20px"
        >
          {isChile && (
            <Box
              gridColumn={{ xs: "span 12", md: "span 3" }}
              gridRow="span 2"
              backgroundColor={colors.primary[200]}
              pb="15px"
              display="flex"
              flexDirection="column"
              sx={{ justifySelf: "start", width: "400px", height: "1617px" }}
            >
              <Typography
                variant="h4"
                fontWeight="600"
                sx={{ marginBottom: "15px" }}
                color={colors.primary[100]}
              >
                DISTRIBUCIÓN GEOGRÁFICA DE PARTICIPANTES
              </Typography>
              <Box flex="1" minHeight="370px">
                <GeographyChart data={mapData} />
              </Box>
            </Box>
          )}

          <Box
            gridColumn={{ xs: "span 12", md: isChile ? "span 9" : "span 12" }}
            gridRow="span 2"
            backgroundColor={colors.primary[200]}
            display="flex"
            flexDirection="column"
            sx={{ minWidth: 0 }}
          >
            <Box pb="15px">
              <Typography variant="h4" fontWeight="600" color={colors.primary[100]}>
                {pageTitle}
              </Typography>
            </Box>

            <Box sx={{ pb: 6 }} display="flex" flexDirection="column" gap="14px">
              {cards.map((card) => (
                <TargetMet
                  key={card.id}
                  title={card.title}
                  variant="dash"
                  fullWidth
                  bgColor={colors.primary[200]}
                  sx={{ borderRadius: "18px" }}
                  titleWrap
                  stats={card.stats}
                  columns={2}
                  disableParticipantsDetail={!canSeeParticipantsList}
                  participantsStatKey="participantes"
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </PageGuard>
  );
};

export default Regiones;
